import React, { useEffect, useMemo, useState } from "react";
import CustomDropDown from "../DropDown";
import { ResponsiveState } from "../../utils/responsiveState";

interface OnePaginationProps {
  totalItems: number;
  itemsPerPageOptions: number[];
  onPageChange: any;
  onItemsPerPageChange: any;
  pageNo?: number;
  initialItemsPerPage?: number;
  className?: string;
  rightmodalPagination?: boolean;
}

const OnePagination: React.FC<OnePaginationProps> = ({
  totalItems,
  itemsPerPageOptions,
  onPageChange,
  onItemsPerPageChange,
  rightmodalPagination = false,
  pageNo = 1,
  className = "",
  initialItemsPerPage,
}) => {
  const [currentPage, setCurrentPage] = useState(pageNo);
  const [itemsPerPage, setItemsPerPage] = useState(
    initialItemsPerPage || itemsPerPageOptions[0]
  );
  const { isLgScreen } = ResponsiveState();
  
  // Keep local state in sync when parent updates (e.g. async restore / filter reset)
  React.useEffect(() => {
    if (typeof pageNo === "number") setCurrentPage(pageNo);
  }, [pageNo]);

  React.useEffect(() => {
    const next =
      initialItemsPerPage != null ? initialItemsPerPage : itemsPerPageOptions[0];
    setItemsPerPage(next);
  }, [initialItemsPerPage, itemsPerPageOptions]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / itemsPerPage)),
    [totalItems, itemsPerPage]
  );

  useEffect(() => {
    // Keep internal state in sync with parent-driven page changes
    // (e.g., when search/tab change resets pagination).
    const nextPage = Math.min(Math.max(1, pageNo), totalPages);
    setCurrentPage(nextPage);
  }, [pageNo, totalPages]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    onPageChange({ currentPage: pageNumber, itemsPerPage });
  };

  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newItemsPerPage = Number(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    onItemsPerPageChange({ currentPage: 1, itemsPerPage: newItemsPerPage });
  };

  const renderPageNumbers = () => {
    // Always show exactly 7 slots for page numbers/ellipsis
    const visibleSlots = 7;
    let pages: (number | '...')[] = [];

    if (totalPages <= visibleSlots) {
      // If total pages is less than visible slots, show all pages
      pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // Always show first and last page
      pages.push(1);
      
      if (currentPage <= 3) {
        // Near start: show 1, 2, 3, 4, 5, ..., totalPages
        pages.push(2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near end: show 1, ..., totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages
        pages.push('...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // Middle: show 1, ..., currentPage-1, currentPage, currentPage+1, ..., totalPages
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return (
      <div className="flex items-center gap-2 min-w-max">
        {pages.map((page, index) => {
          if (page === "...") {
            return (
              <span
                key={`page-${index}`}
                className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-[12px] sm:text-sm font-Open font-medium text-[#777777]"
                aria-hidden="true"
              >
                …
              </span>
            );
          }

          return (
            <button
              key={`page-${index}`}
              onClick={() => handlePageChange(page as number)}
              className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-md text-[12px] sm:text-sm font-Open font-medium transition-colors ${
                currentPage === page
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {page}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={`${
        rightmodalPagination
          ? "flex flex-col gap-4"
          : "flex flex-col gap-4 sm:gap-5 lg:grid lg:grid-cols-2 lg:gap-0 justify-end items-center"
      } ${className} mt-6 sm:mt-8 px-4 sm:px-6 mb-12 sm:mb-20`}
    >
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <button
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
          className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:hover:bg-gray-200 text-sm font-Open font-medium"
        >
          &lt;
        </button>
        {renderPageNumbers()}
        <button
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
          className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:hover:bg-gray-200 text-sm font-Open font-medium"
        >
          &gt;
        </button>
      </div>
      <div
        className={`flex flex-col sm:flex-row sm:items-center gap-2 pt-2 ${
          rightmodalPagination ? "" : "lg:justify-end"
        }`}
      >
        <div className="relative z-20 flex items-center justify-between sm:justify-start gap-x-2 sm:mr-10">
          <p className="font-Open font-normal text-sm text-[#323232] whitespace-nowrap">
            Items per page
          </p>
          <CustomDropDown
            options={itemsPerPageOptions?.map((option) => ({
              label: option,
              value: option,
            }))}
            onChange={handleItemsPerPageChange}
            value={itemsPerPage}
            selectClassName="!h-[36px] sm:!h-[38px] min-w-[84px]"
            useNativeSelect={isLgScreen}
          />
        </div>
        <p className="font-Open font-semibold text-sm text-[#A4A4A4] whitespace-nowrap">
          Item {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{" "}
          {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
        </p>
      </div>
    </div>
  );
};

export default OnePagination;


